/**
 * VolumeReviewPanel — Pink Team / Red Team review scheduling for ONE
 * volume, with pass/fail findings against that volume's outline sections.
 * Deliberately separate from ColorTeamSchedule.jsx (pursuit-wide
 * White/Gold milestones) — two different scopes, two widgets, rather than
 * overloading one component with both.
 *
 * Pink Team: is RFP compliance achievable?
 * Red Team: is it compliant AND compelling — near print-ready?
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plus, Loader2, CalendarClock, Check, Trash2, AlertCircle } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const COLOR_META = {
  PINK: { label: 'Pink Team', bar: 'bg-pink-400', chip: 'bg-pink-500/20 text-pink-300', desc: 'Is RFP compliance achievable?' },
  RED: { label: 'Red Team', bar: 'bg-red-500', chip: 'bg-red-500/20 text-red-300', desc: 'Compliant AND compelling — print-ready check.' },
};

const VERDICT_META = {
  PASS: { label: 'Pass', tone: 'bg-emerald-500/20 text-emerald-300' },
  FAIL: { label: 'Fail', tone: 'bg-red-500/20 text-red-300' },
  NEEDS_WORK: { label: 'Needs Work', tone: 'bg-amber-500/20 text-amber-300' },
  NOT_REVIEWED: { label: 'Not Reviewed', tone: 'bg-slate-700 text-slate-300' },
};

function fmtDate(iso) {
  if (!iso) return '';
  try { return new Date(iso).toLocaleDateString(); } catch { return ''; }
}

export default function VolumeReviewPanel({ pursuitId, volumeId, sections }) {
  const { getAuthHeaders } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ color: 'PINK', scheduled_date: '', reviewer_emails_raw: '' });
  const [findingDraft, setFindingDraft] = useState({}); // reviewId -> {section_id, verdict, comment}

  const fetchReviews = useCallback(async () => {
    if (!volumeId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews?volume_id=${volumeId}`,
        { headers: getAuthHeaders() }
      );
      if (res.ok) {
        const data = await res.json();
        setReviews(data.reviews || []);
      }
    } finally {
      setLoading(false);
    }
  }, [pursuitId, volumeId, getAuthHeaders]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const createReview = async () => {
    if (!form.scheduled_date) {
      toast.error('Pick a scheduled date');
      return;
    }
    setSaving(true);
    try {
      const reviewer_emails = (form.reviewer_emails_raw || '').split(/[,;\n]/).map((e) => e.trim()).filter(Boolean);
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ color: form.color, scheduled_date: form.scheduled_date, reviewer_emails, volume_id: volumeId }),
      });
      if (!res.ok) throw new Error();
      toast.success(`${COLOR_META[form.color].label} review scheduled`);
      setShowForm(false);
      setForm({ color: 'PINK', scheduled_date: '', reviewer_emails_raw: '' });
      fetchReviews();
    } catch {
      toast.error('Failed to schedule review');
    } finally {
      setSaving(false);
    }
  };

  const updateStatus = async (review, status) => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${review.id}`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (!res.ok) throw new Error();
      fetchReviews();
    } catch {
      toast.error('Failed to update status');
    }
  };

  const removeReview = async (review) => {
    try {
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${review.id}`, {
        method: 'DELETE', headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error();
      fetchReviews();
    } catch {
      toast.error('Failed to remove review');
    }
  };

  const saveFinding = async (reviewId) => {
    const draft = findingDraft[reviewId];
    if (!draft?.section_id || !draft?.verdict) {
      toast.error('Pick a section and verdict');
      return;
    }
    try {
      const section = sections?.find((s) => s.id === draft.section_id);
      const res = await fetch(`${API_URL}/api/pursuits/${pursuitId}/strategy/color-reviews/${reviewId}/findings`, {
        method: 'PATCH',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          section_id: draft.section_id,
          section_heading: section?.heading || '',
          verdict: draft.verdict,
          comment: draft.comment || '',
        }),
      });
      if (!res.ok) throw new Error();
      setFindingDraft((prev) => ({ ...prev, [reviewId]: { section_id: '', verdict: '', comment: '' } }));
      toast.success('Finding saved');
      fetchReviews();
    } catch {
      toast.error('Failed to save finding');
    }
  };

  if (loading) {
    return <Loader2 className="w-4 h-4 animate-spin text-slate-500 mx-auto" />;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs text-slate-400">Pink/Red Team reviews for this volume</p>
        {!showForm && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setShowForm(true)} data-testid="volume-review-add-btn">
            <Plus className="w-3 h-3 mr-1" /> Schedule
          </Button>
        )}
      </div>

      {showForm && (
        <div className="bg-slate-800/60 rounded p-3 space-y-2">
          <div className="grid grid-cols-2 gap-2">
            <Select value={form.color} onValueChange={(v) => setForm({ ...form, color: v })}>
              <SelectTrigger className="h-8 text-xs bg-slate-900 border-slate-700"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="PINK">Pink Team</SelectItem>
                <SelectItem value="RED">Red Team</SelectItem>
              </SelectContent>
            </Select>
            <Input
              type="date"
              value={form.scheduled_date}
              onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })}
              className="h-8 text-xs bg-slate-900 border-slate-700"
            />
          </div>
          <Input
            placeholder="Reviewer emails (comma separated)"
            value={form.reviewer_emails_raw}
            onChange={(e) => setForm({ ...form, reviewer_emails_raw: e.target.value })}
            className="h-8 text-xs bg-slate-900 border-slate-700"
          />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setShowForm(false)}>Cancel</Button>
            <Button size="sm" className="h-7 text-xs" onClick={createReview} disabled={saving} data-testid="volume-review-save-btn">
              Save
            </Button>
          </div>
        </div>
      )}

      {reviews.length === 0 && !showForm && (
        <p className="text-xs text-slate-500 italic">No Pink/Red reviews scheduled for this volume yet.</p>
      )}

      {reviews.map((r) => {
        const meta = COLOR_META[r.color] || COLOR_META.PINK;
        const draft = findingDraft[r.id] || { section_id: '', verdict: '', comment: '' };
        return (
          <div key={r.id} className="rounded overflow-hidden border border-slate-700" data-testid={`volume-review-${r.id}`}>
            <div className={`${meta.bar} h-1`} />
            <div className="p-2.5 bg-slate-800/40 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2 py-0.5 rounded ${meta.chip}`}>{meta.label}</span>
                  <span className="text-xs text-slate-400">{fmtDate(r.scheduled_date)}</span>
                  <span className="text-[10px] text-slate-500">{r.status}</span>
                </div>
                <div className="flex items-center gap-1">
                  {r.status !== 'COMPLETE' && (
                    <Button size="sm" variant="ghost" className="h-6 text-[10px] text-emerald-400" onClick={() => updateStatus(r, 'COMPLETE')}>
                      <Check className="w-3 h-3 mr-0.5" /> Complete
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-6 w-6 p-0 text-slate-500 hover:text-red-400" onClick={() => removeReview(r)}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 italic">{meta.desc}</p>

              {r.findings?.length > 0 && (
                <div className="space-y-1">
                  {r.findings.map((f) => (
                    <div key={f.id} className="flex items-center gap-2 text-xs bg-slate-900/60 rounded p-1.5">
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${VERDICT_META[f.verdict]?.tone}`}>
                        {VERDICT_META[f.verdict]?.label}
                      </span>
                      <span className="text-slate-300 truncate flex-1">{f.section_heading}</span>
                      {f.comment && <span className="text-slate-500 truncate max-w-[40%]">{f.comment}</span>}
                    </div>
                  ))}
                </div>
              )}

              <div className="flex items-center gap-1.5 pt-1">
                <Select value={draft.section_id} onValueChange={(v) => setFindingDraft((p) => ({ ...p, [r.id]: { ...draft, section_id: v } }))}>
                  <SelectTrigger className="h-7 text-[11px] bg-slate-900 border-slate-700 flex-1"><SelectValue placeholder="Section…" /></SelectTrigger>
                  <SelectContent>
                    {(sections || []).map((s) => (
                      <SelectItem key={s.id} value={s.id}>{s.heading}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={draft.verdict} onValueChange={(v) => setFindingDraft((p) => ({ ...p, [r.id]: { ...draft, verdict: v } }))}>
                  <SelectTrigger className="h-7 text-[11px] bg-slate-900 border-slate-700 w-28"><SelectValue placeholder="Verdict" /></SelectTrigger>
                  <SelectContent>
                    {Object.keys(VERDICT_META).map((v) => (
                      <SelectItem key={v} value={v}>{VERDICT_META[v].label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button size="sm" className="h-7 text-[11px]" onClick={() => saveFinding(r.id)} data-testid={`volume-review-finding-save-${r.id}`}>
                  Add
                </Button>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
